import { ChangeEvent, DragEvent, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
// Import-mode selector (coming soon): re-add Chip, Radio, RadioGroup when re-enabling.
import { ArrowBack, ArrowForward, Close, InsertDriveFileOutlined, UploadFile } from "@mui/icons-material";
import clsx from "clsx";
import { nanoid } from "nanoid";
import { useSnackbar } from "notistack";
import { makeStyles } from "@/compat/muiStyles";
import { useHistory } from "@/compat/router";
import { KeyboardDatePicker } from "@/compat/pickers";
import { useAppSelector } from "@/redux/hooks";
import CardBase from "../../../components/cards/CardBase";
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import useContactsForAppAndSite from "../../../hooks/useContactsForAppAndSite";
import { formatISODateTime } from "../../../utils/dateTimeUtils";
import useUploadRiskAssessment from "../../hooks/mutations/useUploadRiskAssessment";
import { frequencyDropdownValues } from "../../constants/frequencyConstants";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { UPLOAD_RESULT_KEY } from "../../constants/riskAssessmentUploadConstants";
import { baseRoute } from "../../routes";

const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];
const MAX_BYTES = 25 * 1024 * 1024;
// Mirror the backend column limits so the UI enforces them before submit.
const TITLE_MAX = 500;
const DESCRIPTION_MAX = 1500;

const deriveTitle = (fileName: string) => fileName.replace(/\.[^/.]+$/, "");

type UploadItem = {
  id: string;
  file: File;
  title: string;
  titleTouched: boolean;
  description: string;
  // Per-document overrides of the shared defaults (only used when `override` is on).
  override: boolean;
  siteId: string;
  employeeId: string;
  frequencyId: number | "";
  startDate: Date | null;
};

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  sectionHint: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    marginLeft: theme.spacing(1),
  },
  dropzone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(5, 2),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color .15s, background-color .15s",
    "&:hover": { borderColor: theme.palette.secondary.light },
  },
  dropzoneActive: {
    borderColor: theme.palette.secondary.main,
    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
  },
  dropIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(1.5),
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    color: theme.palette.secondary.main,
  },
  dropHint: {
    color: theme.palette.text.secondary,
    maxWidth: 460,
    margin: `${theme.spacing(1)} auto ${theme.spacing(2)}`,
  },
  hiddenInput: { display: "none" },
  block: { marginTop: theme.spacing(3) },
  grid: { marginTop: theme.spacing(1) },
  itemCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  itemHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  fileName: { flex: 1, minWidth: 0, wordBreak: "break-word", fontWeight: 600 },
  fileSize: { color: theme.palette.text.secondary, whiteSpace: "nowrap" },
  overrideToggle: { marginTop: theme.spacing(1) },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
  },
}));

const formatBytes = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export default function UploadRiskAssessment() {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { sites, selectedSite } = useAppSelector((state) => state.account);

  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [siteId, setSiteId] = useState<string>(selectedSite?.externalId ?? "");
  const [employeeId, setEmployeeId] = useState<string>("");
  const [frequencyId, setFrequencyId] = useState<number | "">("");
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploadRiskAssessment = useUploadRiskAssessment();
  // Full contact list (all sites) once; each employee dropdown filters by its own effective site.
  const allContacts = useContactsForAppAndSite({ selectedSiteId: null });
  const contactsList = Object.values(allContacts?.data ?? {}) as any[];

  const raBase = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;

  const employeesForSite = (site: string) =>
    contactsList
      .filter((c) => !site || c.accountIds?.includes(site))
      .sort((a, b) => (a.firstName ?? "").localeCompare(b.firstName ?? ""));

  const siteOptions = Object.values(sites ?? {});

  const addFiles = (incoming: FileList | File[]) => {
    const additions: UploadItem[] = [];
    Array.from(incoming).forEach((file) => {
      const isValidType = ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));
      if (!isValidType) {
        enqueueSnackbar(`${file.name}: only PDF or Word documents are accepted.`, { variant: "error" });
        return;
      }
      if (file.size > MAX_BYTES) {
        enqueueSnackbar(`${file.name}: exceeds the 25 MB limit.`, { variant: "error" });
        return;
      }
      additions.push({
        id: nanoid(6),
        file,
        title: deriveTitle(file.name),
        titleTouched: false,
        description: "",
        override: false,
        siteId: "",
        employeeId: "",
        frequencyId: "",
        startDate: null,
      });
    });

    if (additions.length)
      setItems((prev) => {
        const seen = new Set(prev.map((it) => `${it.file.name}-${it.file.size}`));
        return [...prev, ...additions.filter((it) => !seen.has(`${it.file.name}-${it.file.size}`))];
      });
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const onFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = "";
  };

  const updateItem = (id: string, patch: Partial<UploadItem>) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems((prev) => prev.filter((it) => it.id !== id));

  const toggleOverride = (id: string) =>
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        if (it.override) return { ...it, override: false };
        // Seed the per-document fields from the shared defaults so only differences need editing.
        return { ...it, override: true, siteId, employeeId, frequencyId, startDate };
      })
    );

  // Effective values used at submit: the item's own overrides, or the shared defaults.
  const effective = (it: UploadItem) =>
    it.override
      ? { site: it.siteId, employee: it.employeeId, frequency: it.frequencyId, startDate: it.startDate }
      : { site: siteId, employee: employeeId, frequency: frequencyId, startDate };

  const canSubmit =
    items.length > 0 &&
    !isSubmitting &&
    items.every((it) => it.title.trim()) &&
    items.every((it) => {
      const e = effective(it);
      return !!e.site && !!e.employee && e.frequency !== "" && !!e.startDate;
    });

  const onContinue = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const created: { id?: number; name: string }[] = [];
      for (const item of items) {
        const e = effective(item);
        const result: any = await uploadRiskAssessment.mutateAsync({
          file: item.file,
          title: item.title.trim(),
          description: item.description.trim(),
          siteExternalId: e.site,
          assignedEmployeeExternalId: e.employee,
          frequency: e.frequency,
          startDate: formatISODateTime(e.startDate),
        });
        created.push({ id: result?.id, name: result?.title ?? item.title.trim() });
      }
      sessionStorage.setItem(UPLOAD_RESULT_KEY, JSON.stringify(created));
      history.push(`${raBase}/add/upload/complete`);
    } catch (err: any) {
      console.error(err);
      const data = err?.response?.data;
      const status = err?.response?.status;
      const apiMessage = typeof data === "string" ? data : data?.message;
      // Show the specific reason for client errors (bad file, validation, missing field);
      // keep server errors generic so we don't surface stack traces to the user.
      const message =
        status && status < 500 && apiMessage?.trim()
          ? apiMessage
          : "Could not save the risk assessment — something went wrong on the server. Please try again.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <CardBase title="Upload Assessment" description="Upload one or more existing Word or PDF documents.">
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Upload Documents
        </Typography>

        <div
          className={clsx(classes.dropzone, { [classes.dropzoneActive]: dragActive })}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx"
            className={classes.hiddenInput}
            onChange={onFileInput}
          />
          <div className={classes.dropIcon}>
            <UploadFile />
          </div>
          <Typography variant="h6">Drag &amp; drop your documents here</Typography>
          <Typography variant="body2" className={classes.dropHint}>
            Upload your existing documents and we will save them in the centralised register. PDF and Word files are
            accepted, maximum 25 MB.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Browse files
          </Button>
        </div>

        {items.length > 0 && (
          <div className={classes.block}>
            <Divider />
            <Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginTop: 16 }}>
              Details
              <Typography component="span" variant="caption" className={classes.sectionHint}>
                applied to all documents unless customised below
              </Typography>
            </Typography>
            <Grid container spacing={2} className={classes.grid}>
              <Grid item xs={12} md={6}>
                <ResponsiveSelect
                  size="small"
                  required
                  fullWidth
                  label="Select Site"
                  options={siteOptions}
                  optionValueKey="externalId"
                  optionLabelKey="name"
                  value={siteId}
                  onChange={(e: any) => {
                    setSiteId(e.target.value);
                    setEmployeeId("");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ResponsiveSelect
                  size="small"
                  required
                  fullWidth
                  label="Assign to Employee"
                  options={employeesForSite(siteId)}
                  optionValueKey="externalId"
                  optionLabelFunction={(x: any) => `${x.firstName} ${x.lastName}`}
                  value={employeeId}
                  onChange={(e: any) => setEmployeeId(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ResponsiveSelect
                  size="small"
                  required
                  fullWidth
                  label="Review Frequency"
                  options={frequencyDropdownValues}
                  optionValueKey="value"
                  optionLabelKey="label"
                  value={frequencyId}
                  onChange={(e: any) => setFrequencyId(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <KeyboardDatePicker
                  format="dd/MM/yyyy"
                  size="small"
                  required
                  fullWidth
                  label="Assessment date"
                  inputVariant="outlined"
                  value={startDate}
                  onChange={(date: Date | null) => setStartDate(date)}
                />
              </Grid>
            </Grid>
          </div>
        )}

        {items.map((item) => (
          <div className={classes.itemCard} key={item.id}>
            <div className={classes.itemHeader}>
              <InsertDriveFileOutlined color="secondary" />
              <Typography variant="body2" className={classes.fileName}>
                {item.file.name}
              </Typography>
              <Typography variant="caption" className={classes.fileSize}>
                {formatBytes(item.file.size)}
              </Typography>
              <IconButton size="small" aria-label="remove" onClick={() => removeItem(item.id)}>
                <Close fontSize="small" />
              </IconButton>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  label="Title"
                  value={item.title}
                  onChange={(e) => updateItem(item.id, { title: e.target.value, titleTouched: true })}
                  slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
                  helperText={`Defaults to the file name. ${item.title.length}/${TITLE_MAX}`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description"
                  placeholder="Add an optional description for this assessment."
                  value={item.description}
                  onChange={(e) => updateItem(item.id, { description: e.target.value })}
                  slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX } }}
                  helperText={`Optional. ${item.description.length}/${DESCRIPTION_MAX}`}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              className={classes.overrideToggle}
              control={
                <Switch
                  size="small"
                  color="secondary"
                  checked={item.override}
                  onChange={() => toggleOverride(item.id)}
                />
              }
              label="Use different details for this document"
            />

            {item.override && (
              <Grid container spacing={2} className={classes.grid}>
                <Grid item xs={12} md={6}>
                  <ResponsiveSelect
                    size="small"
                    required
                    fullWidth
                    label="Select Site"
                    options={siteOptions}
                    optionValueKey="externalId"
                    optionLabelKey="name"
                    value={item.siteId}
                    onChange={(e: any) => updateItem(item.id, { siteId: e.target.value, employeeId: "" })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ResponsiveSelect
                    size="small"
                    required
                    fullWidth
                    label="Assign to Employee"
                    options={employeesForSite(item.siteId)}
                    optionValueKey="externalId"
                    optionLabelFunction={(x: any) => `${x.firstName} ${x.lastName}`}
                    value={item.employeeId}
                    onChange={(e: any) => updateItem(item.id, { employeeId: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ResponsiveSelect
                    size="small"
                    required
                    fullWidth
                    label="Review Frequency"
                    options={frequencyDropdownValues}
                    optionValueKey="value"
                    optionLabelKey="label"
                    value={item.frequencyId}
                    onChange={(e: any) => updateItem(item.id, { frequencyId: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <KeyboardDatePicker
                    format="dd/MM/yyyy"
                    size="small"
                    required
                    fullWidth
                    label="Start Date"
                    inputVariant="outlined"
                    value={item.startDate}
                    onChange={(date: Date | null) => updateItem(item.id, { startDate: date })}
                  />
                </Grid>
              </Grid>
            )}
          </div>
        ))}

        <div className={classes.footer}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={() => history.push(`${raBase}/add`)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
            disabled={!canSubmit}
            onClick={onContinue}
          >
            Continue
          </Button>
        </div>
      </CardBase>
    </Container>
  );
}
